import { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Container,
  Typography,
  Menu,
  MenuItem,
  Select
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React from 'react';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Fermer le drawer et le menu services lors d'un changement de route
  React.useEffect(() => {
    setMobileOpen(false);
    setServicesAnchorEl(null);
  }, [location.pathname]);

  const serviceItems = [
    { text: t('nav.tourism'), path: '/services/tourisme' },
    { text: t('nav.airportTransport'), path: '/services/transport-aeroport' },
    { text: t('nav.concierge'), path: '/services/conciergerie' },
    { text: t('nav.wedding'), path: '/services/mariage' },
    { text: t('nav.event'), path: '/services/evenementiel' }
  ];

  const menuItems = [
    // { text: t('nav.home'), path: '/' },
    { text: t('nav.fleet'), path: '/fleet' },
    { text: t('nav.services'), path: '/services', hasSubmenu: true },
    { text: t('nav.events'), path: '/events' },
    { text: t('nav.corporate'), path: '/corporate' },
    { text: t('nav.reservation'), path: '/reservation' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleServicesClick = (event) => {
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchorEl(null);
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const isActive = (path) => {
    if (path === '/services') {
      return serviceItems.some(item => location.pathname === item.path);
    }
    return location.pathname === path;
  };

  const drawer = (
    <List sx={{ backgroundColor: 'white', height: '100%' }}>
      {menuItems.map((item) => (
        item.hasSubmenu ? (
          <Box key={item.text}>
            <ListItem 
              button 
              onClick={handleServicesClick}
              sx={{
                color: isActive(item.path) ? theme.palette.secondary.main : 'black',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                py: 2,
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  color: theme.palette.secondary.main,
                }
              }}
            >
              <ListItemText 
                primary={item.text} 
                sx={{
                  textAlign: 'center',
                  '& .MuiTypography-root': {
                    fontWeight: isActive(item.path) ? 600 : 500,
                    fontSize: '1rem',
                    letterSpacing: 0.5,
                  }
                }}
              />
              <KeyboardArrowDownIcon />
            </ListItem>
            <Menu
              anchorEl={servicesAnchorEl}
              open={Boolean(servicesAnchorEl)}
              onClose={handleServicesClose}
              onClick={handleDrawerToggle}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  backgroundColor: 'white',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              {serviceItems.map((service) => (
                <MenuItem
                  key={service.text}
                  component={Link}
                  to={service.path}
                  onClick={handleServicesClose}
                  sx={{
                    color: isActive(service.path) ? theme.palette.secondary.main : 'black',
                    py: 1.5,
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      color: theme.palette.secondary.main,
                    }
                  }}
                >
                  {service.text}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : (
          <ListItem 
            button 
            component={Link} 
            to={item.path} 
            key={item.text}
            onClick={handleDrawerToggle}
            sx={{
              color: isActive(item.path) ? theme.palette.secondary.main : 'black',
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              py: 2,
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                color: theme.palette.secondary.main,
              }
            }}
          >
            <ListItemText 
              primary={item.text}
              sx={{
                textAlign: 'center',
                '& .MuiTypography-root': {
                  fontWeight: isActive(item.path) ? 600 : 500,
                  fontSize: '1rem',
                  letterSpacing: 0.5,
                }
              }}
            />
          </ListItem>
        )
      ))}
      <ListItem disablePadding>
        <ListItemButton sx={{ justifyContent: 'center' }}>
          <Select
            value={i18n.language}
            onChange={handleLanguageChange}
            variant="standard"
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="fr">Français</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </Select>
        </ListItemButton>
      </ListItem>
    </List>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          height: { md: '120px' }
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            sx={{ 
              justifyContent: 'space-between',
              minHeight: { md: '120px' },
              px: { xs: 2, md: 4 }
            }}
          >
            <Box 
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <img 
                src="/images/logo.png" 
                alt="Logo" 
                style={{ 
                  width: '150px',
                  maxWidth: "unset"
                }}
              />
            </Box>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ color: 'black' }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {menuItems.map((item) => (
                  item.hasSubmenu ? (
                    <Box key={item.text}>
                      <Button
                        onClick={handleServicesClick}
                        sx={{
                          color: isActive(item.path) ? theme.palette.secondary.main : 'black',
                          fontWeight: isActive(item.path) ? 600 : 500,
                          fontSize: '1rem',
                          textTransform: 'none',
                          letterSpacing: 0.5,
                          px: 2,
                          textAlign: 'center',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.secondary.main,
                          }
                        }}
                        endIcon={<KeyboardArrowDownIcon />}
                      >
                        {item.text}
                      </Button>
                      <Menu
                        anchorEl={servicesAnchorEl}
                        open={Boolean(servicesAnchorEl)}
                        onClose={handleServicesClose}
                        PaperProps={{
                          sx: {
                            mt: 1.5,
                            backgroundColor: 'white',
                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                          }
                        }}
                      >
                        {serviceItems.map((service) => (
                          <MenuItem
                            key={service.text}
                            component={Link}
                            to={service.path}
                            onClick={handleServicesClose}
                            sx={{
                              color: isActive(service.path) ? theme.palette.secondary.main : 'black',
                              py: 1.5,
                              justifyContent: 'center',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                color: theme.palette.secondary.main,
                              }
                            }}
                          >
                            {service.text}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  ) : (
                    <Button
                      key={item.text}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: isActive(item.path) ? theme.palette.secondary.main : 'black',
                        textTransform: 'none',
                        fontWeight: isActive(item.path) ? 600 : 500,
                        fontSize: '1rem',
                        letterSpacing: 0.5,
                        px: 2,
                        textAlign: 'center',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: theme.palette.secondary.main,
                        }
                      }}
                    >
                      {item.text}
                    </Button>
                  )
                ))}
                <Select
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  variant="standard"
                  sx={{ 
                    minWidth: 100,
                    '& .MuiSelect-select': {
                      color: 'black',
                    }
                  }}
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        disableScrollLock={false}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: false,
          BackdropProps: {
            onClick: () => {
              handleDrawerToggle();
            }
          }
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: 'white'
          },
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar sx={{ minHeight: { md: '120px' } }} />
    </>
  );
};

export default Navbar;
